import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Lightbulb } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'step-1', title: 'Step 1: Create your account' },
  { id: 'step-2', title: 'Step 2: Access the Dashboard' },
  { id: 'step-3', title: 'Step 3: Set up Brain AI' },
  { id: 'step-4', title: 'Step 4: Choose your AI employee' },
  { id: 'step-5', title: 'Step 5: Start a conversation' },
  { id: 'step-6', title: 'Step 6: Explore power-ups' },
  { id: 'step-7', title: 'Step 7: Set up automations' },
  { id: 'step-8', title: 'Step 8: Connect integrations' },
  { id: 'good-luck', title: 'Good luck!' },
];

const relatedArticles = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'Login to SweetPilot', href: '/doc/article/login-to-sweetpilot' },
  { title: 'Plans and pricing', href: '/doc/article/pricing-and-plan' },
  { title: 'Brain AI explained', href: '/doc/collections/brain-ai' },
  { title: 'SweetPilot AI Employees', href: '/employees' },
];

interface TipBoxProps {
  children: React.ReactNode;
  isDark: boolean;
}

function TipBox({ children, isDark }: TipBoxProps) {
  return (
    <div className={`flex gap-3 p-4 rounded-xl my-6 ${
      isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'
    }`}>
      <Lightbulb className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
      <div className={`text-sm ${isDark ? 'text-purple-200' : 'text-purple-800'}`}>
        {children}
      </div>
    </div>
  );
}

export default function GettingStartedArticle() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Get Started with SweetPilot - Complete Setup Guide',
    description: 'Step-by-step guide to setting up your SweetPilot account, configuring Brain AI, choosing AI employees, and making the most of automations and integrations.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/getting-started' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>How to Get Started with SweetPilot - Complete Setup Guide | SweetPilot Help Center</title>
        <meta name="description" content="Step-by-step guide to setting up your SweetPilot account. Learn to configure Brain AI, choose AI employees, set up automations, and connect integrations." />
        <meta name="keywords" content="SweetPilot setup, getting started SweetPilot, SweetPilot tutorial, Brain AI setup, AI employees configuration, SweetPilot onboarding, business automation setup" />
        <meta property="og:title" content="How to Get Started with SweetPilot - Complete Setup Guide" />
        <meta property="og:description" content="Step-by-step guide to setting up your SweetPilot account with Brain AI, AI employees, and automations." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/getting-started" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Getting Started with SweetPilot" />
        <meta name="twitter:description" content="Complete setup guide for SweetPilot - Brain AI, AI employees, and automations." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/getting-started" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
        {/* Main Content */}
        <div className="flex-1 max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="mb-8">
            <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              <li>
                <Link
                  to="/docs"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  All Collections
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <Link
                  to="/doc/collections/getting-started"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  Getting started
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li className={isDark ? 'text-white' : 'text-slate-900'}>
                How to get started with SweetPilot
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              How to get started with SweetPilot
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              A step-by-step guide to setting up your SweetPilot account and making the most of your AI employees.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            <p className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Welcome to SweetPilot! This guide will walk you through everything you need to know to get started with your AI-powered workforce. By the end of this guide, you'll be ready to leverage AI employees to streamline your business operations.
            </p>

            {/* Step 1: Create your account */}
            <h2 id="step-1" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 1: Create your account
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              First, you'll need to create a SweetPilot account. Visit our{' '}
              <Link
                to="/login"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                login page
              </Link>{' '}
              and sign up using your email or Google account.
            </p>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              During registration, you'll:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Enter your full name</li>
              <li>Provide your email address</li>
              <li>Create a secure password</li>
            </ul>
            <TipBox isDark={isDark}>
              <strong>Tip:</strong> For detailed instructions on creating your account, check out our{' '}
              <Link
                to="/doc/article/login-to-sweetpilot"
                className={`font-medium underline ${isDark ? 'text-purple-300 hover:text-purple-200' : 'text-purple-700 hover:text-purple-800'}`}
              >
                Login to SweetPilot
              </Link>{' '}
              article.
            </TipBox>

            {/* Step 2: Access the Dashboard */}
            <h2 id="step-2" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 2: Access the Dashboard
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Once you've created your account, you'll be directed to your Dashboard. This is your central hub for:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Viewing all AI employees</strong> available to you</li>
              <li><strong>Monitoring your credit balance</strong> and usage</li>
              <li><strong>Accessing recent conversations</strong> and tasks</li>
              <li><strong>Quick actions</strong> like generating content or analyzing data</li>
            </ul>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Take a moment to explore the Dashboard layout. You'll find the sidebar navigation on the left, which gives you quick access to all SweetPilot features.
            </p>

            {/* Step 3: Set up Brain AI */}
            <h2 id="step-3" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 3: Set up Brain AI
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Brain AI is your central knowledge base that helps personalize AI employee responses to match your business context. Setting it up early will significantly improve the quality of outputs.
            </p>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To set up Brain AI:
            </p>
            <ol className={`list-decimal pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Click on <strong>"Brain"</strong> in the sidebar navigation</li>
              <li>Add your <strong>business information</strong> (company name, industry, target audience)</li>
              <li>Upload relevant <strong>documents, snippets, or webpages</strong></li>
              <li>Connect your <strong>integrations</strong> for additional context</li>
            </ol>
            <TipBox isDark={isDark}>
              <strong>Tip:</strong> The more information you add to Brain AI, the better your AI employees will understand your business and provide relevant, on-brand responses.
            </TipBox>

            {/* Step 4: Choose your AI employee */}
            <h2 id="step-4" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 4: Choose your AI employee
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot offers specialized AI employees, each designed for specific tasks:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Nova</strong> - Customer support and communication</li>
              <li><strong>Atlas</strong> - Data analysis and insights</li>
              <li><strong>Echo</strong> - Copywriting and content creation</li>
              <li><strong>Pixel</strong> - Image generation and design</li>
              <li><strong>Sage</strong> - Research and knowledge gathering</li>
              <li>And many more...</li>
            </ul>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Browse through the{' '}
              <Link
                to="/dashboard/employees"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Employees page
              </Link>{' '}
              to see all available AI employees and their capabilities.
            </p>

            {/* Step 5: Start a conversation */}
            <h2 id="step-5" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 5: Start a conversation
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To begin working with an AI employee:
            </p>
            <ol className={`list-decimal pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Click on the employee card from your Dashboard</li>
              <li>You'll enter a chat interface where you can communicate with your AI employee</li>
              <li>Type your request or question in the message box</li>
              <li>The AI will respond based on your Brain AI context and its specialized training</li>
            </ol>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              You can customize responses by adjusting:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Tone</strong> - Professional, casual, friendly, etc.</li>
              <li><strong>Length</strong> - Short, medium, or detailed responses</li>
              <li><strong>Language</strong> - Choose from multiple supported languages</li>
            </ul>

            {/* Step 6: Explore power-ups */}
            <h2 id="step-6" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 6: Explore power-ups
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Power-ups are pre-built instant tasks that help you accomplish common goals quickly. Each AI employee has their own set of power-ups designed for their specialty.
            </p>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Examples of power-ups include:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Generate blog post</strong> - Create full blog articles from a topic</li>
              <li><strong>Email response</strong> - Draft professional email replies</li>
              <li><strong>Social media pack</strong> - Generate posts for multiple platforms</li>
              <li><strong>SEO audit</strong> - Analyze and optimize your content</li>
            </ul>
            <TipBox isDark={isDark}>
              <strong>Tip:</strong> Power-ups are designed for quick, one-click results. Use them when you need something done fast without extensive back-and-forth conversation.
            </TipBox>

            {/* Step 7: Set up automations */}
            <h2 id="step-7" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 7: Set up automations
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Automations allow AI employees to work in the background without your direct involvement. Once configured, they run on schedules or triggers you define.
            </p>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To set up an automation:
            </p>
            <ol className={`list-decimal pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Look for the <strong>zap icon</strong> on employee cards that support automations</li>
              <li>Click on it to access the automation setup wizard</li>
              <li>Configure the trigger (schedule, event, etc.)</li>
              <li>Set up the required integrations</li>
              <li>Review and activate</li>
            </ol>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Learn more about available automations in our{' '}
              <Link
                to="/doc/collections/automations"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Automations documentation
              </Link>.
            </p>

            {/* Step 8: Connect integrations */}
            <h2 id="step-8" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Step 8: Connect integrations
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot integrates with many popular tools and platforms to enhance your workflow:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Slack</strong> - Receive notifications and interact with AI employees</li>
              <li><strong>Trello</strong> - Sync tasks and project boards</li>
              <li><strong>Google Workspace</strong> - Access documents and calendars</li>
              <li><strong>Social media platforms</strong> - Schedule and publish content</li>
              <li>And many more...</li>
            </ul>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To connect integrations, go to{' '}
              <Link
                to="/dashboard/integrations"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Settings &gt; Integrations
              </Link>{' '}
              and follow the connection wizard for each service.
            </p>
            <TipBox isDark={isDark}>
              <strong>Tip:</strong> Connecting integrations unlocks the full potential of automations and allows Brain AI to gather more context about your business.
            </TipBox>

            {/* Good luck! */}
            <h2 id="good-luck" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Good luck!
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              You're all set to start using SweetPilot! Remember:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Feed Brain AI with your business information for better results</li>
              <li>Experiment with different AI employees to find the best fit for your tasks</li>
              <li>Use power-ups for quick wins and automations for recurring tasks</li>
              <li>Check out our documentation for detailed guides on each feature</li>
            </ul>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              If you have any questions or need assistance, our support team is always here to help. Reach out at{' '}
              <a
                href="mailto:help@sweetpilot.com"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                help@sweetpilot.com
              </a>{' '}
              or visit our{' '}
              <Link
                to="/help"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Help Center
              </Link>.
            </p>
            <p className={`text-lg font-medium mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Welcome to the future of work. Let's build something amazing together!
            </p>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              How to get started
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🚀</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
      </StaticPageLayout>
    </>
  );
}
